import useAsyncFn from 'react-use/lib/useAsyncFn';

import { getErrorMessage, useAuth } from '@spotify/confidence-core-react';

import type { SignupAttribution } from './attribution';

export type { SignupAttribution } from './attribution';

export enum Region {
  RegionEu = 'REGION_EU',
  RegionUs = 'REGION_US',
}
export type AuthConnection = 'googleAuthConnection' | 'passwordAuthConnection';

type AuthConnectionConfig = Record<string, unknown>;

export enum BillingDetailsTaxType {
  EuVat = 'EU_VAT',
  GbVat = 'GB_VAT',
  UsEin = 'US_EIN',
  NoVat = 'NO_VAT',
  AdNrt = 'AD_NRT',
  IsVat = 'IS_VAT',
  RsPib = 'RS_PIB',
  ChVat = 'CH_VAT',
  TrTin = 'TR_TIN',
  UaVat = 'UA_VAT',
  CaBn = 'CA_BN',
  MxRfc = 'MX_RFC',
  IlVat = 'IL_VAT',
  ArCuit = 'AR_CUIT',
  BoTin = 'BO_TIN',
  ClTin = 'CL_TIN',
  CoNit = 'CO_NIT',
  UyRuc = 'UY_RUC',
  VeRif = 'VE_RIF',
  IdNpwp = 'ID_NPWP',
  MyFrp = 'MY_FRP',
  PhTin = 'PH_TIN',
  SgGst = 'SG_GST',
  ThVat = 'TH_VAT',
  VnTin = 'VN_TIN',
  KrBrn = 'KR_BRN',
  TwVat = 'TW_VAT',
  JpTrn = 'JP_TRN',
}

export type BillingDetails = {
  displayName: string;
  email: string;
};

export type CreateAccountRequest = {
  displayName: string;
  loginId: string;
  allowedLoginEmailDomains?: string[];
  adminEmail?: string;
  region: Region;
  authConnections: Record<AuthConnection, AuthConnectionConfig>[];
  billingDetails?: BillingDetails;
  marketingOptIn?: boolean;
  signupAttribution?: SignupAttribution;
};

export const expiredSessionMessage =
  'Your sign-in session expired. Sign in again to continue.';

export class SignupSessionExpiredError extends Error {
  constructor() {
    super(expiredSessionMessage);
    this.name = 'SignupSessionExpiredError';
  }
}

const signInRequiredErrorCodes = [
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'login_required',
  'missing_refresh_token',
] as const;

export const isSignInRequiredError = (error: unknown): boolean => {
  const errorCode =
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof error.error === 'string'
      ? error.error
      : undefined;

  const errorMessage = getErrorMessage(error, '');

  return signInRequiredErrorCodes.some(
    code => errorCode === code || errorMessage.includes(code),
  );
};

export function useCreateAccount(inviteId?: string) {
  const { getAccessTokenSilently } = useAuth();

  return useAsyncFn(
    async (params: CreateAccountRequest) => {
      const { billingDetails, marketingOptIn, signupAttribution, ...account } =
        params;
      let accessToken: string;
      try {
        accessToken = await getAccessTokenSilently();
      } catch (error) {
        if (isSignInRequiredError(error)) {
          throw new SignupSessionExpiredError();
        }
        throw error instanceof Error
          ? error
          : new Error(
              getErrorMessage(error, 'Unable to retrieve access token'),
            );
      }
      if (!accessToken) {
        throw new SignupSessionExpiredError();
      }
      const url = new URL('https://onboarding.confidence.dev/v1/accounts');
      if (inviteId) {
        url.searchParams.set('invite', `accountInvites/${inviteId}`);
      }

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          account,
          billingDetails,
          marketingOptIn,
          ...(signupAttribution
            ? { signup_attribution: signupAttribution }
            : undefined),
        }),
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message);
      }
      return json;
    },
    [getAccessTokenSilently, inviteId],
  );
}
